import type AsideDrawer from '../aside-drawer/AsideDrawer.js'
import type { AsideDrawerWidthChangeEvent } from '../aside-drawer/AsideDrawer.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { lockBodyScroll, unlockBodyScroll } from '../common/body-scroll-lock.js'
import { SlotController } from '../common/controllers/SlotController.js'
import { observe } from '../common/decorators/observe.js'
import { NordEvent } from '../common/events.js'
import { getFocusedElement } from '../common/focus.js'

import componentStyle from '../common/styles/Component.css'
import style from './Aside.css'
import '../tooltip/Tooltip.js'

let resizeTooltipIdCounter = 0

export type AsideHandleClick = 'toggle' | 'close' | 'none'
export type AsideHandleMode = 'col-resize' | 'pointer' | 'default'

/**
 * Precomputed aside geometry — the host's bounding rect and computed
 * style. Hoisted once per coalesced frame and threaded into the cap
 * computations so a single frame doesn't re-read layout multiple times.
 * @internal
 */
interface AsideGeometry {
  rect: DOMRect
  styles: CSSStyleDeclaration
}

/**
 * Event dispatched when the aside's active drawer changes — on open,
 * switch, and close. `activeDrawer` is the new active drawer id (empty
 * string when all drawers closed); `previousDrawer` is the prior one.
 */
export class AsideActiveDrawerChangeEvent extends NordEvent {
  declare activeDrawer: string
  declare previousDrawer: string

  constructor(activeDrawer: string, previousDrawer: string) {
    super('active-drawer-change')
    Object.defineProperties(this, {
      activeDrawer: { value: activeDrawer, enumerable: true, writable: false },
      previousDrawer: { value: previousDrawer, enumerable: true, writable: false },
    })
  }
}

/**
 * Event dispatched when a drawer opens from the fully-closed state.
 * `activeDrawer` is the id of the drawer that opened.
 */
export class AsideDrawerOpenEvent extends NordEvent {
  declare activeDrawer: string

  constructor(activeDrawer: string) {
    super('drawer-open')
    Object.defineProperty(this, 'activeDrawer', {
      value: activeDrawer,
      enumerable: true,
      writable: false,
    })
  }
}

/**
 * Event dispatched when all drawers close. `previousDrawer` is the id of
 * the drawer that was open before closing.
 */
export class AsideDrawerCloseEvent extends NordEvent {
  declare previousDrawer: string

  constructor(previousDrawer: string) {
    super('drawer-close')
    Object.defineProperty(this, 'previousDrawer', {
      value: previousDrawer,
      enumerable: true,
      writable: false,
    })
  }
}

/**
 * Event dispatched when the active drawer's width changes. `drawerId`
 * names the specific drawer whose width changed (unlike the
 * active-drawer-change event, which describes a transition between
 * drawers); `width` is the new width in px.
 */
export class AsideDrawerResizeEvent extends NordEvent {
  declare drawerId: string
  declare width: number

  constructor(drawerId: string, width: number) {
    super('drawer-resize')
    Object.defineProperties(this, {
      drawerId: { value: drawerId, enumerable: true, writable: false },
      width: { value: width, enumerable: true, writable: false },
    })
  }
}

/**
 * Event dispatched when the resize handle's interaction mode changes.
 * `mode` is the new mode; `previous` is the prior mode.
 */
export class AsideHandleModeChangeEvent extends NordEvent {
  declare mode: AsideHandleMode
  declare previous: AsideHandleMode

  constructor(mode: AsideHandleMode, previous: AsideHandleMode) {
    super('handle-mode-change')
    Object.defineProperties(this, {
      mode: { value: mode, enumerable: true, writable: false },
      previous: { value: previous, enumerable: true, writable: false },
    })
  }
}

const NAV_RESIZE_STEP = 30
const RESIZE_DRAG_THRESHOLD = 4
// Minimum strip of viewport (in px) that must remain visible on the
// inline-start side when the drawer is being resized. Only applies when
// the layout is NOT in rail collapse mode — in that case the nav's
// right edge does the gating instead. See `getLeftCapViewportX`.
const DRAWER_VIEWPORT_LEFT_OFFSET = 54

// Selector matching natively-focusable / explicitly-tabbable elements,
// used to find focusable descendants inside the floating drawer for the
// narrow-viewport focus trap.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  'details > summary:first-of-type',
].join(',')

const mediaQuery
  = typeof matchMedia !== 'undefined'
    ? matchMedia('(min-width: 768px)')
    : ({
        matches: false,
        addEventListener() {
          /* noop */
        },
        removeEventListener() {
          /* noop */
        },
      } as unknown as MediaQueryList)

const warnedTriggers = new WeakSet<HTMLElement>()

/**
 * Container for the right-side rail + drawer pattern in the app shell.
 *
 * @status new
 * @category layout
 * @slot rail - Free-form content placed in the rail column.
 * @slot tooltip-divider - Tooltip anchored to the resize handle on hover.
 * @slot - Default slot for `<nord-aside-drawer>` panels.
 *
 * @fires {AsideActiveDrawerChangeEvent} active-drawer-change - Dispatched
 * when `active-drawer` changes, with `activeDrawer` / `previousDrawer`
 * properties. Fires on every transition including open, switch, and close.
 * @fires {AsideDrawerOpenEvent} drawer-open - Dispatched when transitioning
 * from no active drawer to an active one (i.e. a drawer opened from the
 * closed state). Has an `activeDrawer` property.
 * @fires {AsideDrawerCloseEvent} drawer-close - Dispatched when all drawers
 * close (active-drawer went from a non-empty value to empty). Has a
 * `previousDrawer` property.
 * @fires {AsideDrawerResizeEvent} drawer-resize - Dispatched when the active
 * drawer's width changes, with `drawerId` (the specific drawer that changed)
 * and `width` properties. Unlike active-drawer-change, this names a single
 * drawer by id rather than describing a transition between drawers.
 * @fires {AsideHandleModeChangeEvent} handle-mode-change - Dispatched when
 * the resize handle's mode changes (`col-resize` / `pointer` / `default`).
 * Mirrors the current value to the host's `data-handle-mode` attribute so
 * consumers can also read it synchronously. Has `mode` / `previous` properties.
 *
 * @cssprop [--n-aside-rail-width=48px] - Controls the inline-size of the rail column.
 * @cssprop [--n-aside-rail-gap=var(--n-space-s)] - Controls the gap between the rail and the active drawer.
 * @cssprop [--n-aside-rail-background-color=var(--n-color-surface)] - Controls the background color of the rail column.
 * @cssprop [--n-aside-background-color=var(--n-color-background)] - Controls the background color of the aside container (matches the layout's main area by default).
 * @cssprop [--n-aside-drawers-margin-inline-start=0] - Inline-start margin of the drawers wrapper — creates a gap between the layout's main area and the active drawer card. The resize handle follows this margin so it stays at the drawer's edge.
 * @cssprop [--n-aside-rail-narrow-block-size=56px] - Block-size of the rail when it renders as a bottom bar at <768px.
 * @cssprop [--n-aside-rail-narrow-padding-inline=var(--n-space-m)] - Inline padding inside the bottom rail at <768px.
 * @cssprop [--n-aside-rail-narrow-padding-block=var(--n-space-s)] - Block padding inside the bottom rail at <768px.
 * @cssprop [--n-aside-rail-narrow-border-block-start-width=1px] - Top border width of the bottom rail at <768px. Set to 0 to remove.
 * @cssprop [--n-aside-rail-narrow-border-color=var(--n-aside-rail-border-color)] - Top border color of the bottom rail at <768px.
 * @cssprop [--n-aside-drawer-floating-inset-block-start=var(--n-layout-header-size, 60px)] - Distance from the viewport top to the floating drawer at <768px (clears the layout header).
 * @cssprop [--n-aside-drawer-floating-inset-inline=0px] - Inline gap between the floating drawer and each viewport edge at <768px. Default 0 = full viewport width.
 * @cssprop [--n-aside-drawer-slide-duration=300ms] - Duration of the slide-up animation when a drawer opens at <768px.
 * @cssprop [--n-aside-drawer-slide-easing=cubic-bezier(0.4, 0, 0.2, 1)] - Easing of the slide-up animation when a drawer opens at <768px.
 * @cssprop [--n-aside-drawer-floating-inset-block-end=calc(rail-size - bottom-offset)] - Distance from the viewport bottom to the floating drawer at <768px. By default the drawer's bottom edge meets the rail's top edge.
 * @cssprop [--n-aside-drawer-floating-bottom-offset=0] - Subtracts from the floating drawer's `inset-block-end` to let the drawer extend past the rail's top edge (visual overlap). Default 0.
 * @cssprop [--n-aside-backdrop-background=rgb(0 0 0 / 0.4)] - Background color of the backdrop behind the floating drawer at <768px.
 * @cssprop [--n-aside-z-backdrop=calc(var(--n-index-top-bar) + 2)] - z-index of the backdrop at <768px (above the sticky footer so it dims the footer too).
 * @cssprop [--n-aside-z-drawer-floating=calc(var(--n-index-top-bar) + 3)] - z-index of the floating drawer overlay at <768px (above the backdrop).
 * @cssprop [--n-aside-z-rail-narrow=calc(var(--n-index-top-bar) + 4)] - z-index of the bottom rail at <768px (above the drawer so it remains tappable).
 */
@customElement('nord-aside')
export default class Aside extends LitElement {
  static styles = [componentStyle, style]

  /**
   * The id of the currently-active `<nord-aside-drawer>`. Empty string
   * means no drawer is open. Setting to an unknown / disabled id is a
   * silent no-op.
   */
  @property({ reflect: true, attribute: 'active-drawer' }) activeDrawer: string = ''

  /**
   * Width of the rail column in px.
   */
  @property({ reflect: true, type: Number, attribute: 'rail-width' }) railWidth: number = 48

  /**
   * Gap between rail and the active drawer panel in px.
   */
  @property({ reflect: true, type: Number, attribute: 'rail-gap' }) railGap: number = 0

  /**
   * Controls what clicking (no-drag) the resize handle does.
   * Drag-for-resize is independent of this attribute.
   */
  @property({ reflect: true, attribute: 'handle-click' }) handleClick: AsideHandleClick = 'toggle'

  /**
   * Session-only memory of the most-recent non-empty `active-drawer`.
   * Does not persist across page reload. Used by the resize-handle
   * click in `toggle` mode to reopen the last drawer.
   * @internal
   */
  @state() private lastDrawer: string = ''

  @state() private isDragging: boolean = false

  @state() private wideScreen: boolean = mediaQuery.matches

  @state() private hasRailItems: boolean = false

  @query('.n-resize') private handleEl?: HTMLButtonElement
  @query('.n-resize-tooltip') private resizeTooltipEl?: HTMLElement

  private tooltipSlot = new SlotController(this, 'tooltip-divider')
  private readonly resizeTooltipId = `n-aside-resize-tip-${++resizeTooltipIdCounter}`

  private childObserver?: MutationObserver

  private dragStartX = 0
  private hasDragged = false

  render() {
    const handleState = this.handleInteractive
    const showTooltip = handleState.tooltip && !this.isDragging
    // Reflect the resize handle's current mode on the host so consumers
    // (story / app code) can react — e.g. hide a "Drag to resize" line
    // when the active drawer is pinned or fullscreen.
    if (this.dataset.handleMode !== handleState.cursor) {
      const previous = this.dataset.handleMode as AsideHandleMode | undefined
      this.dataset.handleMode = handleState.cursor
      if (previous !== undefined) {
        this.dispatchEvent(new AsideHandleModeChangeEvent(handleState.cursor, previous))
      }
    }
    return html`
      <div class=${classMap({ 'n-aside': true, 'n-dragging': this.isDragging })} part="aside">
        <button
          type="button"
          class="n-resize"
          part="resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize drawer"
          aria-disabled=${handleState.enabled ? 'false' : 'true'}
          aria-describedby=${showTooltip ? this.resizeTooltipId : nothing}
          data-handle-mode=${handleState.cursor}
          tabindex=${handleState.dragOk ? '0' : '-1'}
          ?hidden=${!this.wideScreen || !this.activeDrawer}
          @pointerdown=${this.startDragging}
          @pointermove=${this.isDragging ? this.handleDrag : this.handleResizeHoverMove}
          @pointerleave=${this.handlePointerLeave}
          @pointerup=${this.stopDragging}
          @keydown=${this.handleKeyboardResize}
        ></button>
        ${this.tooltipSlot.hasContent
          ? html`
              <nord-tooltip
                id=${this.resizeTooltipId}
                position="inline-start"
                class="n-resize-tooltip"
              >
                <slot name="tooltip-divider"></slot>
              </nord-tooltip>
            `
          : nothing}
        <div class="n-aside-rail" part="rail">
          <slot name="rail" @slotchange=${this.handleRailSlotChange}></slot>
        </div>
        <div class="n-aside-drawers" part="drawers">
          <slot></slot>
        </div>
        <div
          class="n-aside-backdrop"
          part="backdrop"
          aria-hidden="true"
          @click=${this.handleBackdropClick}
        ></div>
      </div>
    `
  }

  private handleRailSlotChange = (e: Event) => {
    const slot = e.target as HTMLSlotElement
    const next = slot.assignedElements({ flatten: true }).length > 0
    if (next !== this.hasRailItems) {
      this.hasRailItems = next
      this.syncNarrowState()
    }
  }

  private handleBackdropClick = () => {
    if (this.activeDrawer) {
      this.lastDrawer = this.activeDrawer
      this.activeDrawer = ''
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.applyRailVars()
    this.observeChildren()
    mediaQuery.addEventListener('change', this.handleMediaChange)
    this.addEventListener('width-change', this.handleDrawerWidthChange)
    this.addEventListener('keydown', this.handleHostKeyDown)
    window.addEventListener('resize', this.handleViewportResize, { passive: true })
    // When slotted into <nord-layout slot="drawer">, push the aside's
    // outer width into `--n-layout-drawer-inline-size` so the layout's
    // drawer slot grows/shrinks with the active drawer.
    queueMicrotask(() => {
      this.attachLayoutSync()
      this.syncNarrowState()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.childObserver?.disconnect()
    this.childObserver = undefined
    mediaQuery.removeEventListener('change', this.handleMediaChange)
    this.removeEventListener('width-change', this.handleDrawerWidthChange)
    this.removeEventListener('keydown', this.handleHostKeyDown)
    window.removeEventListener('resize', this.handleViewportResize)
    this.clearNarrowState()
    this.detachLayoutSync()
  }

  private handleMediaChange = (e: MediaQueryListEvent) => {
    this.wideScreen = e.matches
    this.syncNarrowState()
    // Recompute the layout's drawer inline-size — at narrow it's pinned
    // to 0 so the aside overlay doesn't reserve space; going wide must
    // restore the rail + drawer outer width so the layout reveals the
    // drawer slot column.
    this.syncLayoutDrawerSize()
  }

  /**
   * Esc closes the active drawer when it's floating (narrow + open).
   * Wide-screen handling is unchanged — Esc has no effect at desktop.
   */
  private handleHostKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape')
      return
    if (this.wideScreen)
      return
    if (!this.activeDrawer)
      return
    // A nested overlay (dropdown / modal) inside the drawer may have
    // already handled this Esc and called preventDefault — don't steal it
    // to close the drawer underneath.
    if (e.defaultPrevented)
      return
    this.lastDrawer = this.activeDrawer
    this.activeDrawer = ''
    // Only swallow the event once we've actually closed a drawer, so an
    // Esc that does nothing here still propagates to ancestor handlers.
    e.stopPropagation()
  }

  /**
   * Compute narrow-mode state from viewport + slot + layout context, and
   * reflect two attributes on the aside host AND its containing layout:
   *
   * - `data-narrow-rail-bottom`: rail is a bottom-fixed bar.
   *   Active when narrow + rail has slotted items.
   * - `data-narrow-floating-drawer`: drawer is a floating overlay.
   *   Active when narrow + a drawer is active (regardless of collapse-mode
   *   or fullscreen — fullscreen is neutralized at narrow).
   *
   * Also writes `--n-aside-rail-narrow-inset-block-end` on the host so
   * the rail lifts above the layout's sticky-footer when one is present.
   * Suspends `--n-layout-drawer-inline-size` (sets to 0) at narrow so
   * the layout's main area doesn't reserve drawer space.
   */
  /**
   * Tracks whether THIS aside currently holds a body-scroll lock so we
   * don't double-lock or leak a lock across disconnect. The lock helper
   * itself is ref-counted globally; this flag is per-instance bookkeeping.
   */
  private holdsBodyScrollLock = false

  private syncNarrowState() {
    const layout = this.hostLayout
    const isNarrow = !this.wideScreen
    const isRailBottom = isNarrow && this.hasRailItems
    const isFloatingDrawer = isNarrow && !!this.activeDrawer

    this.toggleAttribute('data-narrow-rail-bottom', isRailBottom)
    this.toggleAttribute('data-narrow-floating-drawer', isFloatingDrawer)
    layout?.toggleAttribute('data-narrow-rail-bottom', isRailBottom)
    layout?.toggleAttribute('data-narrow-floating-drawer', isFloatingDrawer)

    // Rail is always pinned to the viewport bottom at narrow — including
    // when there's a sticky footer (rail sits on top of the footer, its
    // higher z-index keeps it tappable). Clear any prior lift value.
    this.style.removeProperty('--n-aside-rail-narrow-inset-block-end')

    // Lock body scroll while a drawer is floating at narrow. iOS Safari
    // doesn't honor `overflow: hidden` on `<body>` for touch scroll, so
    // the helper uses `position: fixed; top: -<scrollY>px` — the
    // canonical fix. Per-instance flag prevents double-lock; the helper
    // itself ref-counts globally so multiple overlay components coexist.
    if (isFloatingDrawer && !this.holdsBodyScrollLock) {
      lockBodyScroll()
      this.holdsBodyScrollLock = true
    }
    else if (!isFloatingDrawer && this.holdsBodyScrollLock) {
      unlockBodyScroll()
      this.holdsBodyScrollLock = false
    }

    // Focus management for the narrow floating overlay. The drawer becomes
    // a modal-like dialog: capture the activating trigger, move focus in,
    // trap Tab, and restore focus on close. Only at narrow + floating —
    // the wide-screen docked drawer is part of the page flow and never
    // traps focus.
    if (isFloatingDrawer && !this.floatingFocusActive) {
      this.enterFloatingFocus()
    }
    else if (!isFloatingDrawer && this.floatingFocusActive) {
      this.exitFloatingFocus()
    }
  }

  /**
   * Tracks whether the narrow floating-drawer focus trap is currently
   * engaged, so `syncNarrowState` only enters / exits on a real
   * transition (mirrors `holdsBodyScrollLock`).
   */
  private floatingFocusActive = false

  /**
   * The element that was focused when the floating drawer opened (the
   * activating trigger). Focus is returned here on close if it's still
   * connected.
   */
  private floatingFocusReturnElement: HTMLElement | null = null

  /**
   * The drawer currently held in the floating focus trap. Cached so
   * `exitFloatingFocus` can clear the contract `floating` flag and the
   * fallback `tabindex` even after `activeDrawer` has already changed.
   */
  private trappedDrawer: AsideDrawer | null = null

  /**
   * Engage the floating-drawer focus trap. SSR-safe: only runs when a
   * document is available (it's only ever called from `syncNarrowState`,
   * which runs in client lifecycle, but guard anyway).
   */
  private enterFloatingFocus() {
    if (typeof document === 'undefined')
      return
    const drawer = this.activeDrawerEl
    if (!drawer)
      return

    this.floatingFocusActive = true
    this.trappedDrawer = drawer
    // Flip the contract property so the drawer host advertises itself as
    // a modal dialog (role=dialog + aria-modal) while floating.
    drawer.floating = true

    // Capture the activating trigger (shadow-aware) so we can return
    // focus to it on close.
    const focused = getFocusedElement(document)
    this.floatingFocusReturnElement = focused instanceof HTMLElement ? focused : null

    document.addEventListener('keydown', this.handleFloatingFocusKeydown, true)

    // Defer focus to rAF: syncNarrowState runs synchronously during a Lit
    // property set, before updateDrawerVisibility has removed `hidden` in
    // cases where the aside connects with activeDrawer already set in HTML.
    // focus() on a hidden element is a browser no-op — screen readers never
    // receive the dialog entry announcement. The rAF ensures the DOM is
    // visible before we move focus.
    requestAnimationFrame(() => {
      const focusTarget = this.firstFocusableInDrawer(drawer)
      if (focusTarget) {
        focusTarget.focus()
      }
      else {
        drawer.tabIndex = -1
        drawer.focus()
      }
    })
  }

  /**
   * Release the floating-drawer focus trap and restore focus to the
   * captured trigger if it's still connected.
   */
  private exitFloatingFocus() {
    this.floatingFocusActive = false
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.handleFloatingFocusKeydown, true)
    }

    const drawer = this.trappedDrawer
    if (drawer) {
      drawer.floating = false
      // Remove the fallback tabindex we may have added so the drawer
      // doesn't linger as a tab stop once it's docked again.
      if (drawer.getAttribute('tabindex') === '-1') {
        drawer.removeAttribute('tabindex')
      }
    }
    this.trappedDrawer = null

    const returnElement = this.floatingFocusReturnElement
    this.floatingFocusReturnElement = null
    if (returnElement && returnElement.isConnected) {
      returnElement.focus()
    }
  }

  /**
   * Trap Tab / Shift+Tab within the floating drawer. Focusable elements
   * are queried live each keystroke because the drawer's content lives in
   * slotted light DOM and can change. Wraps from last to first and back.
   */
  private handleFloatingFocusKeydown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab')
      return
    const drawer = this.trappedDrawer
    if (!drawer)
      return
    const focusable = this.focusableElementsInDrawer(drawer)
    if (focusable.length === 0) {
      // Nothing tabbable inside — keep focus pinned on the drawer host.
      e.preventDefault()
      drawer.tabIndex = -1
      drawer.focus()
      return
    }
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = getFocusedElement(document)
    if (e.shiftKey) {
      if (active === first || !this.drawerContainsFocus(drawer, active)) {
        e.preventDefault()
        last.focus()
      }
    }
    else if (active === last || !this.drawerContainsFocus(drawer, active)) {
      e.preventDefault()
      first.focus()
    }
  }

  /**
   * Whether the currently-focused element lives inside the drawer's
   * subtree (light DOM or nested shadow roots). Used to redirect focus
   * back into the trap when Tab would otherwise escape it.
   */
  private drawerContainsFocus(drawer: AsideDrawer, active: Element | undefined): boolean {
    return active != null && (drawer === active || drawer.contains(active))
  }

  /**
   * Query the drawer's currently-focusable descendants in DOM order.
   * Slotted light DOM, so a live `querySelectorAll` reflects the latest
   * content. Filters out disabled / hidden / non-tabbable elements.
   */
  private focusableElementsInDrawer(drawer: AsideDrawer): HTMLElement[] {
    const candidates = drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    return Array.from(candidates).filter(element => this.isFocusable(element))
  }

  private firstFocusableInDrawer(drawer: AsideDrawer): HTMLElement | null {
    return this.focusableElementsInDrawer(drawer)[0] ?? null
  }

  private isFocusable(element: HTMLElement): boolean {
    if (element.hasAttribute('disabled'))
      return false
    if (element.getAttribute('aria-hidden') === 'true')
      return false
    if (element.tabIndex < 0)
      return false
    // `offsetParent` is null for `display: none` (and fixed-position
    // edge cases handled well enough here) — skip non-rendered elements.
    return element.offsetParent !== null || element.getClientRects().length > 0
  }

  private clearNarrowState() {
    const layout = this.hostLayout
    this.removeAttribute('data-narrow-rail-bottom')
    this.removeAttribute('data-narrow-floating-drawer')
    layout?.removeAttribute('data-narrow-rail-bottom')
    layout?.removeAttribute('data-narrow-floating-drawer')
    this.style.removeProperty('--n-aside-rail-narrow-inset-block-end')
    // Release any body-scroll lock this instance held so disconnects
    // don't leak the lock count.
    if (this.holdsBodyScrollLock) {
      unlockBodyScroll()
      this.holdsBodyScrollLock = false
    }
    // Tear down the floating focus trap so disconnect doesn't leak the
    // document-level keydown listener.
    if (this.floatingFocusActive) {
      this.exitFloatingFocus()
    }
  }

  /**
   * Re-broadcast a drawer's local `width-change` as the aside's
   * `drawer-resize` so consumers only need one listener.
   */
  private handleDrawerWidthChange = (e: Event) => {
    const widthChangeEvent = e as AsideDrawerWidthChangeEvent
    const target = e.target
    if (!(target instanceof HTMLElement) || target.tagName !== 'NORD-ASIDE-DRAWER') {
      return
    }
    this.dispatchEvent(new AsideDrawerResizeEvent(target.id, widthChangeEvent.width))
    this.syncLayoutDrawerSize()
  }

  /**
   * Cached references to the listened-to layout. Cleared on disconnect.
   */
  private syncedLayout?: HTMLElement

  private get hostLayout(): HTMLElement | null {
    // Use the static `slot` HTML attribute rather than `assignedSlot.name`.
    // `assignedSlot` is set by the browser after the slot-assignment
    // algorithm runs, which can race with the layout's async first render
    // (Lit) or Vue's mount cycle — leaving it null when our handlers fire.
    // The `slot` attribute is set declaratively in HTML and is reliable.
    if (this.getAttribute('slot') !== 'drawer')
      return null
    return this.closest('nord-layout') as HTMLElement | null
  }

  /**
   * When slotted into `<nord-layout slot="drawer">`, push the aside's
   * outer width into `--n-layout-drawer-inline-size` so the layout's
   * drawer slot grows/shrinks to fit. No-op when the aside isn't slotted
   * into a layout.
   */
  private attachLayoutSync() {
    const layout = this.hostLayout
    if (!layout)
      return
    this.syncedLayout = layout
    this.syncLayoutDrawerSize()
    // React to the layout's nav state — both the discrete events fired
    // at the start of an open/close, and the through-transition frames
    // caught via a ResizeObserver on the nav element itself. Both feed
    // `scheduleLeftCapReconcile` so a docked / fullscreen drawer follows
    // the freed / consumed left-side space in lockstep.
    layout.addEventListener('nav-open-change', this.handleLayoutNavChange)
    layout.addEventListener('nav-resize', this.handleLayoutNavChange)
    const nav = layout.querySelector('nord-navigation')
    if (nav && typeof ResizeObserver !== 'undefined') {
      this.navResizeObserver = new ResizeObserver(() => this.scheduleLeftCapReconcile())
      this.navResizeObserver.observe(nav)
    }
  }

  private detachLayoutSync() {
    if (!this.syncedLayout)
      return
    this.syncedLayout.removeEventListener('nav-open-change', this.handleLayoutNavChange)
    this.syncedLayout.removeEventListener('nav-resize', this.handleLayoutNavChange)
    this.navResizeObserver?.disconnect()
    this.navResizeObserver = undefined
    this.syncedLayout = undefined
  }

  /**
   * Push the aside's intended outer width (rail + active drawer + margins
   * + gap) into `--n-layout-drawer-inline-size` so the layout's drawer
   * slot wrapper grows/shrinks to fit. Also pins
   * `--n-layout-drawer-min-inline-size` to the rail's outer width so
   * narrow viewports (where the layout would otherwise drop the content
   * margin and overlay the rail) still reserve room for the rail.
   */
  private syncLayoutDrawerSize() {
    const layout = this.hostLayout
    if (!layout)
      return
    // At narrow viewports the aside renders as an overlay (bottom rail,
    // floating drawer) — it must not reserve inline space in the
    // layout's main area.
    if (!this.wideScreen) {
      layout.style.setProperty('--n-layout-drawer-inline-size', '0px')
      layout.style.setProperty('--n-layout-drawer-min-inline-size', '0px')
      return
    }
    let total = this.railWidth
    if (this.activeDrawer) {
      const active = this.findDrawer(this.activeDrawer)
      const drawerWidth = active?.width ?? active?.defaultWidth ?? 0
      if (drawerWidth) {
        const style = active ? getComputedStyle(active) : null
        const marginStart = style ? Number.parseFloat(style.marginInlineStart) || 0 : 0
        const marginEnd = style ? Number.parseFloat(style.marginInlineEnd) || 0 : 0
        // `.n-aside-drawers` carries the inline-start margin that creates
        // the gap between the layout's main area and the drawer card.
        // Counted into the slot width so the main content's margin-inline-end
        // grows to reveal that gap.
        const drawersWrapper = this.renderRoot.querySelector('.n-aside-drawers')
        const drawersMarginStart = drawersWrapper
          ? Number.parseFloat(getComputedStyle(drawersWrapper).marginInlineStart) || 0
          : 0
        total += drawerWidth + marginStart + marginEnd + drawersMarginStart + this.railGap
      }
    }
    layout.style.setProperty('--n-layout-drawer-inline-size', `${total}px`)
    layout.style.setProperty('--n-layout-drawer-min-inline-size', `${this.railWidth}px`)
  }

  /**
   * Surface `rail-width` / `rail-gap` as custom properties so the slotted
   * rail content can react without the host needing a re-render.
   */
  @observe('railWidth')
  @observe('railGap')
  protected applyRailVars() {
    this.style.setProperty('--_n-aside-rail-width', `${this.railWidth}px`)
    this.style.setProperty('--_n-aside-rail-gap', `${this.railGap}px`)
    this.syncLayoutDrawerSize()
  }

  @observe('activeDrawer')
  protected handleActiveDrawerChange(previousDrawer: string, activeDrawer: string) {
    if (previousDrawer === activeDrawer) {
      return
    }

    if (activeDrawer) {
      const drawer = this.findDrawer(activeDrawer)
      if (!drawer || drawer.disabled) {
        this.activeDrawer = previousDrawer
        return
      }
      this.lastDrawer = activeDrawer
    }

    this.updateDrawerVisibility()
    this.syncLayoutDrawerSize()
    this.scheduleLeftCapReconcile()
    this.syncNarrowState()

    this.dispatchEvent(new AsideActiveDrawerChangeEvent(activeDrawer, previousDrawer))

    // Specialized lifecycle events for consumers that only care about
    // "did any drawer just open?" / "did all drawers just close?".
    if (activeDrawer && !previousDrawer) {
      this.dispatchEvent(new AsideDrawerOpenEvent(activeDrawer))
    }
    else if (!activeDrawer && previousDrawer) {
      this.dispatchEvent(new AsideDrawerCloseEvent(previousDrawer))
    }
  }

  private findDrawer(id: string): AsideDrawer | null {
    return id
      ? this.querySelector<AsideDrawer>(
          `:scope > nord-aside-drawer#${CSS.escape(id)}`,
        )
      : null
  }

  private get activeDrawerEl(): AsideDrawer | null {
    return this.activeDrawer ? this.findDrawer(this.activeDrawer) : null
  }

  private updateDrawerVisibility() {
    const drawers = this.querySelectorAll<AsideDrawer>(':scope > nord-aside-drawer')
    drawers.forEach((drawer) => {
      const isActive = !!this.activeDrawer && drawer.id === this.activeDrawer
      drawer.toggleAttribute('hidden', !isActive)
    })
  }

  private observeChildren() {
    this.childObserver = new MutationObserver((mutations) => {
      let needsVisibilityRefresh = false
      let needsFullscreenSync = false

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          needsVisibilityRefresh = true
          needsFullscreenSync = true
          continue
        }
        if (mutation.type !== 'attributes') {
          continue
        }
        const target = mutation.target as HTMLElement
        // subtree:true means we also see attribute mutations on deep
        // descendants — filter to direct nord-aside-drawer children only.
        if (target.tagName !== 'NORD-ASIDE-DRAWER' || target.parentElement !== this) {
          continue
        }
        if (mutation.attributeName === 'id' && mutation.oldValue && this.activeDrawer === mutation.oldValue) {
          this.activeDrawer = target.id
          if (this.lastDrawer === mutation.oldValue) {
            this.lastDrawer = target.id
          }
        }
        if (mutation.attributeName === 'fullscreen' || mutation.attributeName === 'data-animating-fullscreen') {
          needsFullscreenSync = true
        }
        needsVisibilityRefresh = true
      }

      if (needsVisibilityRefresh) {
        if (this.activeDrawer && !this.findDrawer(this.activeDrawer)) {
          this.activeDrawer = ''
        }
        else {
          this.updateDrawerVisibility()
        }
      }
      if (needsFullscreenSync) {
        this.syncFullscreenAttr()
      }
    })

    // subtree:true so attribute changes on direct children (id, disabled,
    // fullscreen, data-animating-fullscreen) fire too. The callback
    // filters to direct `nord-aside-drawer` children, so descendants are
    // ignored. `fullscreen` + `data-animating-fullscreen` drive
    // `syncFullscreenAttr`, which reflects a `[data-fullscreen]` attr on
    // the host so CSS can react reliably (more robust than `:has()`
    // reactivity for attribute changes across some engines).
    this.childObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'disabled', 'fullscreen', 'data-animating-fullscreen'],
      attributeOldValue: true,
    })

    this.updateDrawerVisibility()
    this.syncFullscreenAttr()
  }

  /**
   * Reflect a `data-fullscreen` attribute on the host whenever any
   * direct `<nord-aside-drawer>` child is currently fullscreen OR
   * transitioning into/out of fullscreen. CSS selectors target this
   * attribute to bump the rail above the fullscreen drawer's z-index.
   */
  private syncFullscreenAttr() {
    const drawers = this.querySelectorAll<AsideDrawer>(':scope > nord-aside-drawer')
    let hasFullscreen = false
    drawers.forEach((d) => {
      if (d.hasAttribute('fullscreen') || d.hasAttribute('data-animating-fullscreen')) {
        hasFullscreen = true
      }
    })
    this.toggleAttribute('data-fullscreen', hasFullscreen)
    // Drive the active drawer's fullscreen inline-size from the layout
    // context: when entering fullscreen (or its size requirements change),
    // override AsideDrawer's max-width-based default with the left-cap
    // derived value. When leaving fullscreen, clear the override.
    const active = this.activeDrawerEl
    if (active?.hasAttribute('fullscreen')) {
      this.scheduleLeftCapReconcile()
    }
    else {
      this.clearFullscreenSizeOverride()
    }
  }

  /* ---------------------------------------------
  / DRAG / CLICK ON THE RESIZE HANDLE
  / --------------------------------------------- */

  private startDragging = (e: PointerEvent) => {
    if (e.button !== 0 || !this.wideScreen) {
      return
    }
    const target = e.currentTarget as HTMLElement
    // setPointerCapture throws on synthetic PointerEvents (tests) — the
    // capture is a nice-to-have for real pointers, not load-bearing.
    try {
      target.setPointerCapture(e.pointerId)
    }
    catch {
      /* synthetic event, no real pointer to capture */
    }
    this.isDragging = true
    this.dragStartX = e.clientX
    this.hasDragged = false
    // Cache the aside's geometry for the whole drag: only `clientX`
    // changes between pointermove frames, so the bounding rect (right
    // edge) and computed style (rail/gap inputs, layout floor vars) are
    // stable. Reading them once at drag-start avoids two forced layout
    // reflows per pointermove.
    this.dragGeometry = {
      rect: this.getBoundingClientRect(),
      styles: getComputedStyle(this),
    }
  }

  /**
   * Aside geometry captured at `startDragging` and reused for every
   * pointermove until `stopDragging`. Cleared on drag end.
   */
  private dragGeometry?: AsideGeometry

  private stopDragging = (e?: PointerEvent) => {
    if (!this.isDragging) {
      return
    }
    const wasClick = !this.hasDragged
    this.isDragging = false
    this.hasDragged = false
    this.dragGeometry = undefined
    if (e) {
      const target = e.currentTarget as HTMLElement
      try {
        if (target.hasPointerCapture(e.pointerId)) {
          target.releasePointerCapture(e.pointerId)
        }
      }
      catch {
        /* synthetic event */
      }
    }
    if (wasClick) {
      this.handleHandleClick()
    }
  }

  private handlePointerLeave = (e: PointerEvent) => {
    // If we're mid-drag and the pointer escapes without capture, treat as up.
    if (this.isDragging) {
      this.stopDragging(e)
    }
    this.resizeTooltipEl?.style.removeProperty('--n-resize-tooltip-y')
  }

  /**
   * Follow the pointer vertically so the tooltip floats next to the cursor
   * along the handle's full height instead of staying anchored to its
   * midpoint. Mirrors `<nord-layout>`'s handleResizeHoverMove.
   */
  private handleResizeHoverMove = (e: PointerEvent) => {
    const target = e.currentTarget as HTMLElement | null
    if (!target || !this.resizeTooltipEl) {
      return
    }
    const rect = target.getBoundingClientRect()
    const offset = e.clientY - (rect.top + rect.height / 2)
    this.resizeTooltipEl.style.setProperty('--n-resize-tooltip-y', `${offset}px`)
  }

  private handleDrag = (e: PointerEvent) => {
    if (!this.activeDrawerEl) {
      // Drag with no active drawer: just track that the pointer moved
      // past the threshold so stopDragging treats this as a drag, not click.
      if (Math.abs(e.clientX - this.dragStartX) >= RESIZE_DRAG_THRESHOLD) {
        this.hasDragged = true
      }
      return
    }
    if (!this.hasDragged && Math.abs(e.clientX - this.dragStartX) < RESIZE_DRAG_THRESHOLD) {
      return
    }
    this.hasDragged = true

    const drawer = this.activeDrawerEl
    if (drawer.isPinned) {
      return
    }
    // Geometry was cached at drag-start (only clientX changes mid-drag).
    // Fall back to a live read for the unusual case of a drag without a
    // start (e.g. a synthetic pointermove in a test).
    const geometry = this.dragGeometry ?? {
      rect: this.getBoundingClientRect(),
      styles: getComputedStyle(this),
    }
    // Aside is on the right side: width grows as the pointer moves left.
    const desired = geometry.rect.right - e.clientX - this.railWidth - this.railGap
    // Clamp at the left-cap (nav's right edge OR the viewport-left
    // offset, per collapse-mode rules) so dragging can't push the
    // drawer over the layout's <nord-navigation> sidebar. ALSO reserves
    // `--n-layout-main-min-inline-size` (default 600px at >=1240px)
    // so the layout's main content body never collapses below the floor.
    // Skipped silently when there's no host layout.
    const leftCapWidth = this.drawerWidthAtCapWithMainFloor(geometry)
    const width = leftCapWidth !== undefined ? Math.min(desired, leftCapWidth) : desired
    drawer.setWidth(width, 'drag')
  }

  /**
   * Viewport-x where the drawer's inline-start edge must stop. Unified
   * rule that drives drag, keyboard, and fullscreen sizing:
   *
   * - `collapse-mode="rail"` → `max(0, nav.right)`. Nav stays visible
   *   as a rail, so the cap is the nav's right edge regardless of
   *   open/closed state (rail width vs expanded width).
   * - otherwise → `max(nav.right, DRAWER_VIEWPORT_LEFT_OFFSET)`. The
   *   nav can translate fully off-screen; the 54px viewport floor only
   *   kicks in when the nav is actually off-screen — when the nav is
   *   on-screen and wider than 54, its right edge wins.
   *
   * Returns `undefined` when no host layout is present.
   */
  private getLeftCapViewportX(): number | undefined {
    const layout = this.hostLayout
    if (!layout)
      return undefined
    const nav = layout.querySelector('nord-navigation') as HTMLElement | null
    const navRight = nav ? nav.getBoundingClientRect().right : 0
    return layout.getAttribute('collapse-mode') === 'rail'
      ? Math.max(0, navRight)
      : Math.max(navRight, DRAWER_VIEWPORT_LEFT_OFFSET)
  }

  /**
   * Derive the drawer's maximum inline-size from {@link getLeftCapViewportX}
   * WITHOUT reserving the layout's main-panel floor — the drawer grows
   * all the way to the layout's main-panel edge. Used by fullscreen sizing.
   *
   * Returns `undefined` when no host layout is present.
   */
  private drawerWidthAtCap(geometry?: AsideGeometry): number | undefined {
    return this.computeDrawerWidthAtCap(false, geometry)
  }

  /**
   * Derive the drawer's maximum inline-size from {@link getLeftCapViewportX}
   * WITH the layout's main-panel floor reserved — drag / keyboard resize
   * can never push the main body below `--n-layout-main-min-inline-size`
   * (default `0` below 1240px, `600px` above). Used by drag, keyboard, and
   * the drag-cap reconcile.
   *
   * Returns `undefined` when no host layout is present.
   */
  private drawerWidthAtCapWithMainFloor(geometry?: AsideGeometry): number | undefined {
    return this.computeDrawerWidthAtCap(true, geometry)
  }

  /**
   * Shared core for {@link drawerWidthAtCap} /
   * {@link drawerWidthAtCapWithMainFloor}. Subtracts:
   * - `--n-layout-main-min-inline-size` when `reserveMainMin === true`.
   *
   * Reads the aside's geometry (bounding rect + computed style) once;
   * callers in the same coalesced frame may pass a precomputed
   * {@link AsideGeometry} to avoid redundant layout reads.
   *
   * Returns `undefined` when no host layout is present.
   */
  private computeDrawerWidthAtCap(
    reserveMainMin: boolean,
    geometry?: AsideGeometry,
  ): number | undefined {
    const cap = this.getLeftCapViewportX()
    if (cap === undefined)
      return undefined
    const rect = geometry?.rect ?? this.getBoundingClientRect()
    const styles = geometry?.styles ?? getComputedStyle(this)
    const mainMin = reserveMainMin
      ? this.parseLengthVar(styles, '--n-layout-main-min-inline-size')
      : 0
    return Math.max(0, rect.right - this.railWidth - this.railGap - cap - mainMin)
  }

  private parseLengthVar(styles: CSSStyleDeclaration, name: string): number {
    return Number.parseFloat(styles.getPropertyValue(name)) || 0
  }

  /**
   * Publishes the layout's left-cap (the maximum the fullscreen drawer
   * can grow to without overlapping the nav) as
   * `--_n-aside-drawer-fullscreen-available` on the active drawer.
   *
   * The drawer's `:host([fullscreen])` rule uses CSS `min()` to clamp
   * its own ceiling (`--_n-aside-drawer-fullscreen-max`) by this
   * available value — so a consumer who sets `max-fullscreen-width`
   * higher than the layout cap can never overflow past the main-panel
   * edge. Pure-CSS resolution; this method just publishes the input.
   *
   * Coalesced via rAF so a flurry of nav/viewport/slot events collapses
   * to one write per frame. The host's geometry (bounding rect + computed
   * style) is read once per frame and threaded into both flushes so a
   * single frame never re-reads layout.
   */
  private scheduleLeftCapReconcile = () => {
    if (this.leftCapReconcileScheduled)
      return
    this.leftCapReconcileScheduled = true
    requestAnimationFrame(() => {
      this.leftCapReconcileScheduled = false
      // Read the aside's geometry once for the whole frame — both flushes
      // derive their caps from the same rect + computed style.
      const geometry: AsideGeometry = {
        rect: this.getBoundingClientRect(),
        styles: getComputedStyle(this),
      }
      this.flushFullscreenAvailable(geometry)
      this.flushDragCap(geometry)
    })
  }

  private leftCapReconcileScheduled = false

  private flushFullscreenAvailable(geometry: AsideGeometry) {
    const drawer = this.activeDrawerEl
    if (!drawer)
      return
    const available = this.drawerWidthAtCap(geometry)
    if (available === undefined) {
      drawer.style.removeProperty('--_n-aside-drawer-fullscreen-available')
      return
    }
    // `drawerWidthAtCap` already subtracts the inline-end offset
    // ONCE — which matches the `inset-inline-end` reservation. The
    // fullscreen drawer also needs the same clearance on its INLINE-START
    // (left edge) so its border doesn't paint flush against the nav.
    // Subtract one more `inlineEndOffset` here so the left edge lands at
    // `nav.right + offset` instead of `nav.right`. Additionally subtract
    // the drawer's own `border-inline-start-width` because the slotted
    // <nord-drawer>'s border is drawn outside its inline-size box and
    // would otherwise overflow past the cap by that width.
    const asideStyles = geometry.styles
    // The drawer's own border-inline-start (from `--n-aside-drawer-border-width`)
    // sits outside the slotted nord-drawer's inline-size box, so the
    // available width needs to account for it too. Read from the aside's
    // public token — which inherits down to the drawer.
    const drawerBorder = this.parseLengthVar(asideStyles, '--n-aside-drawer-border-width')
    drawer.style.setProperty(
      '--_n-aside-drawer-fullscreen-available',
      `${Math.max(0, available - drawerBorder)}px`,
    )
  }

  /**
   * If the active drawer's docked width now exceeds the drag cap (e.g.
   * the nav just expanded and the cap tightened), shrink the drawer
   * down to the new cap. Skipped while a drag is in flight so the
   * user's interaction stays unbroken; skipped for fullscreen drawers
   * since fullscreen has its own cap path. Reconciles the same events
   * `flushFullscreenAvailable` listens to.
   */
  private flushDragCap(geometry: AsideGeometry) {
    const drawer = this.activeDrawerEl
    if (!drawer || drawer.hasAttribute('fullscreen') || this.isDragging)
      return
    const cap = this.drawerWidthAtCapWithMainFloor(geometry)
    if (cap === undefined)
      return
    if (drawer.width > cap)
      drawer.setWidth(cap, 'attribute')
  }

  /**
   * Remove the published `--_n-aside-drawer-fullscreen-available` var
   * so the drawer's CSS `min()` rule falls back to `100vw` and the
   * drawer's own `--_n-aside-drawer-fullscreen-max` becomes the
   * effective cap. Called when the drawer leaves fullscreen.
   */
  private clearFullscreenSizeOverride() {
    this.activeDrawerEl?.style.removeProperty('--_n-aside-drawer-fullscreen-available')
  }

  private handleLayoutNavChange = () => {
    this.scheduleLeftCapReconcile()
  }

  private handleViewportResize = () => {
    this.scheduleLeftCapReconcile()
  }

  private navResizeObserver?: ResizeObserver

  /**
   * Apply the `handle-click` matrix. Called by `stopDragging` when the
   * pointer hasn't moved past the drag threshold.
   */
  private handleHandleClick() {
    if (this.handleClick === 'none') {
      return
    }
    const open = !!this.activeDrawer

    if (open) {
      // toggle + close: close (and stash) the active drawer
      this.lastDrawer = this.activeDrawer
      this.activeDrawer = ''
      return
    }

    if (this.handleClick !== 'toggle') {
      return
    }

    // toggle mode, no active drawer → reopen lastDrawer if valid, else
    // open the first DOM-order trigger whose drawer resolves + isn't disabled.
    if (this.lastDrawer) {
      const drawer = this.findDrawer(this.lastDrawer)
      if (drawer && !drawer.disabled) {
        this.activeDrawer = this.lastDrawer
        return
      }
    }
    const fallback = this.findFirstTriggerDrawer()
    if (fallback) {
      this.activeDrawer = fallback
    }
  }

  private findFirstTriggerDrawer(): string | null {
    const triggers = this.querySelectorAll<HTMLElement>('nord-aside-trigger[drawer]')
    for (const trigger of triggers) {
      const id = trigger.getAttribute('drawer')
      if (!id) {
        continue
      }
      const drawer = this.findDrawer(id)
      if (drawer && !drawer.disabled) {
        return id
      }
      if (!drawer && process.env.NODE_ENV !== 'production') {
        if (!warnedTriggers.has(trigger)) {
          warnedTriggers.add(trigger)

          console.warn(
            `<nord-aside-trigger drawer="${id}"> points at a non-existent <nord-aside-drawer> — click is a no-op.`,
          )
        }
      }
    }
    return null
  }

  /* ---------------------------------------------
  / KEYBOARD RESIZE
  / --------------------------------------------- */

  private handleKeyboardResize = (e: KeyboardEvent) => {
    const drawer = this.activeDrawerEl
    if (!drawer || !this.wideScreen) {
      return
    }
    const leftCapWidth = this.drawerWidthAtCapWithMainFloor()
    const cap = (width: number) => leftCapWidth !== undefined ? Math.min(width, leftCapWidth) : width
    // Aside is on the right: ArrowLeft = wider, ArrowRight = narrower.
    switch (e.key) {
      case 'ArrowLeft':
        drawer.setWidth(cap(drawer.width + NAV_RESIZE_STEP), 'keyboard')
        break
      case 'ArrowRight':
        drawer.setWidth(drawer.width - NAV_RESIZE_STEP, 'keyboard')
        break
      case 'Home':
        drawer.setWidth(drawer.minWidth, 'keyboard')
        break
      case 'End':
        drawer.setWidth(cap(drawer.maxWidth), 'keyboard')
        break
      default:
        return
    }
    e.preventDefault()
  }

  /* ---------------------------------------------
  / UNIFIED AFFORDANCE RULE
  / --------------------------------------------- */

  /**
   * "Is the handle interactive right now?" — single computation that
   * drives cursor, tooltip-divider visibility, and `aria-disabled`.
   * @internal
   */
  get handleInteractive(): {
    enabled: boolean
    dragOk: boolean
    clickOk: boolean
    cursor: 'col-resize' | 'pointer' | 'default'
    tooltip: boolean
  } {
    if (!this.wideScreen) {
      return { enabled: false, dragOk: false, clickOk: false, cursor: 'default', tooltip: false }
    }
    const drawer = this.activeDrawerEl
    const open = !!drawer
    const dragOk = open && !drawer!.isPinned && !drawer!.fullscreen

    let clickOk: boolean
    switch (this.handleClick) {
      case 'toggle':
        clickOk = true // toggle works in both states (reopen lastDrawer when closed)
        break
      case 'close':
        clickOk = open
        break
      case 'none':
      default:
        clickOk = false
        break
    }

    const enabled = dragOk || clickOk
    let cursor: 'col-resize' | 'pointer' | 'default'
    if (dragOk) {
      cursor = 'col-resize'
    }
    else if (clickOk) {
      cursor = 'pointer'
    }
    else {
      cursor = 'default'
    }
    const tooltip = enabled && this.tooltipSlot.hasContent
    return { enabled, dragOk, clickOk, cursor, tooltip }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-aside': Aside
  }
}
